import React, { useState } from 'react';
import { Flame, ChevronDown, ChevronRight } from 'lucide-react';
import { resolveBySymbol } from '../constants/instruments';
import { useEngine } from '../context/MarketContext';
import { useAppSelector } from '../store/hooks';
import type { EngineRegime } from '../types';

const WATCHLIST_COLLAPSED_KEY = 'shabba:watchlist:collapsed';

const REGIME_COLORS: Record<EngineRegime, string> = {
  ACTIVE:       '#36c98e',
  accumulation: '#36c98e',
  QUIET:        '#4da3ff',
  neutral:      '#4da3ff',
  DORMANT:      '#7a8693',
  VOLATILE:     '#ff5c5c',
  distribution: '#ff5c5c',
};

const REGIME_LABEL: Record<EngineRegime, string> = {
  ACTIVE:       'Active',
  accumulation: 'Accumulation',
  QUIET:        'Quiet',
  neutral:      'Neutral',
  DORMANT:      'Dormant',
  VOLATILE:     'Volatile',
  distribution: 'Distribution',
};

export function Watchlist({
  selectedSymbol, setSelectedSymbol,
  instruments, allInstruments, onAdd, onRemove,
}: {
  selectedSymbol: string;
  setSelectedSymbol: (s: string) => void;
  instruments: { label: string; secId: string }[];
  allInstruments: { label: string; secId: string }[];
  onAdd: (label: string, secId: string) => void;
  onRemove: (secId: string) => void;
}) {
  const { regimeBySymbol, sprintState } = useEngine();
  const vwapMap = useAppSelector(s => s.chartData.vwap);
  const winnerSet = new Set(sprintState?.winners ?? []);
  const [adding, setAdding] = useState(false);

  // Collapsed state is persisted: a panel that silently reopens on every reload is not
  // really collapsible. Guarded because localStorage throws in private-mode Safari.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(WATCHLIST_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(WATCHLIST_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Counters available to add (in full universe but not in current watchlist)
  const available = allInstruments.filter(
    a => !instruments.some(i => i.secId.toUpperCase() === a.secId.toUpperCase())
  );

  return (
    <div style={{ backgroundColor: '#10151b', borderRadius: 8, border: '1px solid #2a3540', overflow: 'hidden' }}>
      {/* Header */}
      <div
        onClick={toggleCollapsed}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }}
        style={{ padding: '10px 14px 8px', borderBottom: collapsed ? 'none' : '1px solid #10151b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {collapsed ? <ChevronRight size={13} color="#46525e" /> : <ChevronDown size={13} color="#46525e" />}
          <span style={{ fontSize: 11, fontWeight: 700, color: '#e8a33d', letterSpacing: '0.08em' }}>WATCHLIST</span>
          {collapsed && (
            <span style={{ fontSize: 10, color: '#46525e' }}>{instruments.length}</span>
          )}
        </span>
        <button
          onClick={e => { e.stopPropagation(); if (collapsed) setCollapsed(false); setAdding(o => !o); }}
          title={adding ? 'Close' : 'Add counter'}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: adding ? '#ff5c5c' : '#46525e', fontSize: 16, lineHeight: 1,
            padding: '0 2px', display: 'flex', alignItems: 'center',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = adding ? '#ff5c5c' : '#7e8a96')}
          onMouseLeave={e => (e.currentTarget.style.color = adding ? '#ff5c5c' : '#46525e')}
        >
          {adding ? '×' : '+'}
        </button>
      </div>

      {/* Instrument rows */}
      {!collapsed && (
      <div style={{ padding: '4px 6px 6px' }}>
        {instruments.map((it) => {
          const vwapData = vwapMap[it.secId.toUpperCase()];
          const latestVwap = vwapData && vwapData.length > 0 ? vwapData[vwapData.length - 1]?.vwap : null;
          const firstVwap  = vwapData && vwapData.length > 1 ? vwapData[0]?.vwap : null;
          const changePct  = latestVwap != null && firstVwap != null && firstVwap !== 0
            ? ((latestVwap - firstVwap) / firstVwap) * 100
            : null;
          const regime = resolveBySymbol(regimeBySymbol, it.secId);
          const isWinner = winnerSet.has(it.secId) || winnerSet.has(it.label);
          const regimeColor = regime ? REGIME_COLORS[regime.regime] : '#2a3540';
          const regimeTitle = regime ? REGIME_LABEL[regime.regime] : 'No data';
          const isSelected = selectedSymbol === it.secId;

          return (
            <div
              key={it.secId}
              onClick={() => setSelectedSymbol(it.secId)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '7px 8px', borderRadius: 6,
                backgroundColor: isSelected ? 'rgba(77,163,255,0.08)' : 'transparent',
                borderLeft: `2px solid ${isSelected ? '#4da3ff' : 'transparent'}`,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {/* Left: dot + label (truncated) + flame */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
                <div title={regimeTitle} style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: regimeColor, flexShrink: 0 }} />
                <span
                  title={it.label}
                  style={{
                    fontSize: 13, fontWeight: 600, color: isSelected ? '#d7dde4' : '#cbd5e1',
                    maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {it.label}
                </span>
                {isWinner && (
                  <span title="Sprint winner — capital boosted" style={{ lineHeight: 0, flexShrink: 0 }}>
                    <Flame size={11} color="#ff8c3a" />
                  </span>
                )}
              </div>

              {/* Right: Δ% + price + remove (only in picker mode) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {changePct != null && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: changePct >= 0 ? '#36c98e' : '#ff5c5c' }}>
                    {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
                  </span>
                )}
                <span style={{ fontSize: 12, fontWeight: 600, color: '#d7dde4', fontVariantNumeric: 'tabular-nums', minWidth: 52, textAlign: 'right' }}>
                  {latestVwap != null ? latestVwap.toFixed(2) : '—'}
                </span>
                {adding && instruments.length > 1 && (
                  <button
                    onClick={e => { e.stopPropagation(); onRemove(it.secId); }}
                    title={`Remove ${it.label}`}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#2a3540', fontSize: 14, padding: '0 2px', lineHeight: 1, flexShrink: 0,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#ff5c5c')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#2a3540')}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Counter picker panel */}
        {adding && (
          <div style={{ marginTop: 8, padding: '8px', borderRadius: 6, backgroundColor: '#0a0d10', border: '1px solid #2a3540' }}>
            <div style={{ fontSize: 9, color: '#46525e', fontWeight: 700, letterSpacing: '0.06em', marginBottom: 6 }}>ADD COUNTER</div>
            {available.length === 0 ? (
              <div style={{ fontSize: 11, color: '#2a3540', fontStyle: 'italic' }}>All available counters added</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {available.map(a => (
                  <button
                    key={a.secId}
                    onClick={() => { onAdd(a.label, a.secId); }}
                    style={{
                      backgroundColor: '#10151b', border: '1px solid #2a3540',
                      borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#46525e')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#2a3540')}
                  >
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#d7dde4' }}>{a.label}</span>
                    <span style={{ fontSize: 9, color: '#46525e' }}>·{a.secId}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
